/**
 * An amount of money in financial numerals (大写数字), as a cheque or a
 * receipt writes it. ￥1,409.50 is 壹仟肆佰零玖元伍角.
 *
 * Each numeral has a written form of many strokes so that 一 cannot become
 * 十 or 千 with a pen. The rules for where 零 goes and when 整 closes the
 * amount are the People's Bank of China's, in 《正确填写票据和结算凭证的基本
 * 规定》, the first appendix to 《支付结算办法》 (银发〔1997〕393号). The tests
 * hold this module to every example it gives.
 *
 * Where the rules allow either of two forms, this writes the first one they
 * give. ￥1,680.32 is 壹仟陆佰捌拾元零叁角贰分 (the second form drops the
 * 零), and ￥107,000.53 is 壹拾万柒仟元零伍角叁分 (the second moves the 零
 * after the 万). The unit is 元, as the rules list it. The banknotes print 圆.
 */

const numerals = ["零", "壹", "贰", "叁", "肆", "伍", "陆", "柒", "捌", "玖"];

/** The places within a section of four digits, highest first. */
const places = ["仟", "佰", "拾", ""];

/**
 * Whole 元 and up to two places of decimals. Commas, a leading sign and a
 * third decimal place are all refused. None of them has a written form on a
 * cheque.
 */
const amountPattern = /^(\d+)(?:\.(\d{1,2}))?$/;

/**
 * The amount in financial numerals, or nothing for anything that is not an
 * amount of money.
 *
 * 整 closes an amount with no 角 or 分. The rules let it follow 角 as well,
 * and this leaves it off there, as it is left off after 分.
 */
export function financialNumerals(value: string): string | undefined {
  const match = amountPattern.exec(value.trim());
  if (match === null) {
    return undefined;
  }

  const whole = (match[1] ?? "").replace(/^0+/, "");
  const [jiao = 0, fen = 0] = (match[2] ?? "")
    .padEnd(2, "0")
    .split("")
    .map(Number);

  if (whole === "" && jiao === 0 && fen === 0) {
    return "零元整";
  }

  const yuan = whole === "" ? "" : `${wholeNumber(whole)}元`;
  if (jiao === 0 && fen === 0) {
    return `${yuan}整`;
  }

  return yuan + fraction(whole, jiao, fen);
}

/**
 * The 角 and 分 after the 元.
 *
 * A 零 stands between the two where a 0 does. Rule (四) asks for it where
 * the 角 place is 0 and the 分 is not, and the first form rule (三) gives
 * asks for it where the 元 place is 0 and the 角 is not.
 */
function fraction(whole: string, jiao: number, fen: number): string {
  const jiaoSaid =
    jiao === 0
      ? whole === ""
        ? ""
        : "零"
      : `${whole.endsWith("0") ? "零" : ""}${numerals[jiao]}角`;

  return jiaoSaid + (fen === 0 ? "" : `${numerals[fen]}分`);
}

/**
 * A whole number in financial numerals, split at 亿 and then at 万.
 *
 * Splitting at 亿 first is what puts 万 in the right place above it.
 * 1,0001,0000,0000 is 壹万零壹亿, which counting in sections of four from the
 * right would write as 壹万亿零壹亿.
 *
 * @param digits At least one digit, with no leading zeros.
 */
function wholeNumber(digits: string): string {
  if (digits.length > 8) {
    return `${wholeNumber(digits.slice(0, -8))}亿${rest(digits.slice(-8))}`;
  }

  if (digits.length > 4) {
    return `${section(digits.slice(0, -4))}万${rest(digits.slice(-4))}`;
  }

  return section(digits);
}

/**
 * What follows a 亿 or a 万, which begins with 零 where its digits do.
 *
 * ￥100,100 is 壹拾万零壹佰 and ￥100,000,001 is 壹亿零壹. A run of zeros
 * ending the amount writes nothing at all.
 */
function rest(digits: string): string {
  const significant = digits.replace(/^0+/, "");

  if (significant === "") {
    return "";
  }

  return (
    (significant.length < digits.length ? "零" : "") + wholeNumber(significant)
  );
}

/**
 * Up to four digits, with no leading zeros.
 *
 * A run of zeros between two other digits is one 零, as ￥6,007.14 is
 * 陆仟零柒元壹角肆分, and a run ending the section writes nothing. 壹 is
 * written before 拾 even at the head of the amount, where speech drops it:
 * ten 元 is 壹拾元整.
 */
function section(digits: string): string {
  const placed = places.slice(places.length - digits.length);
  let said = "";
  let zeros = false;

  for (const [index, digit] of digits.split("").map(Number).entries()) {
    if (digit === 0) {
      zeros = true;
      continue;
    }

    said += `${zeros ? "零" : ""}${numerals[digit]}${placed[index]}`;
    zeros = false;
  }

  return said;
}

/**
 * How each character of an amount is read.
 *
 * Each is read as the everyday numeral it stands for, so 贰 is `èr` and never
 * `liǎng`, even before 仟 or 万 where 二 would be. Written out here rather
 * than read through the package, which knows the everyday numerals and none of
 * these.
 */
const readings: ReadonlyMap<string, string> = new Map([
  ["零", "líng"],
  ["壹", "yī"],
  ["贰", "èr"],
  ["叁", "sān"],
  ["肆", "sì"],
  ["伍", "wǔ"],
  ["陆", "liù"],
  ["柒", "qī"],
  ["捌", "bā"],
  ["玖", "jiǔ"],
  ["拾", "shí"],
  ["佰", "bǎi"],
  ["仟", "qiān"],
  ["万", "wàn"],
  ["亿", "yì"],
  ["元", "yuán"],
  ["角", "jiǎo"],
  ["分", "fēn"],
  ["整", "zhěng"],
]);

/**
 * The pinyin of an amount {@link financialNumerals} wrote, one syllable to a
 * character and spaced as the tool's other readings are.
 */
export function financialPinyin(hanzi: string): string {
  return hanzi
    .split("")
    .map((character) => readings.get(character) ?? "")
    .join(" ");
}
